import { useState, useContext, useMemo } from 'react'
import { jt, t } from 'ttag'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'

import { Text } from '@h2/components/typography/text'
import pickSalarySvg from '@h2/assets/svg/illustrations/budget/pickYourSalary.svg'
import chooseLifestyleSvg from '@h2/assets/svg/illustrations/budget/chooseYourLifestyle.svg'
import { Globals } from '@h2/globals'
import { Page } from '@h2/common/components/page'
import { api } from '@h2/api'
import { Heading } from '@h2/components/typography/heading'
import { Link } from '@h2/common/components/link'
import { Button } from '@h2/components/buttons/button'
import { Flex } from '@h2/components/layout/flex'
import { Modal } from '@h2/components/containers/modal'
import { BudgetPiggyBankIcon, EditIcon, TrashIcon } from '@h2/components/icons'
import { useNavigator } from '@h2/common/components/hooks/useNavigator'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { Grid } from '@h2/components/containers/grid'
import { PB70T, SG90T } from '@h2/components/colors'
import { Skeleton } from '@h2/components/loading/skeleton'

import { RenameBudgetForm } from './components/renameBudgetForm'

const BoxButton = styled(Button)`
  background-color: initial;
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain !important;
  width: 225px;
  height: 225px;
  margin: 0 auto 10px auto;
  border: none;
  float: left;
  margin-right: 15px;
  span {
    display: none;
  }
`
const CurrentBudgetBox = styled.div`
  border: 1px solid ${PB70T};
  background-color: ${SG90T};
  border-radius: 10px;
  padding: 15px;
`
const ReverseButton = styled(BoxButton)`
  background-image: url(${pickSalarySvg});
`
const ForwardButton = styled(BoxButton)`
  background-image: url(${chooseLifestyleSvg});
`
const PreviousBudget = ({ isLoading, defaultBudget, openModal }) => {
  if (isLoading) {
    return <Skeleton height="75px" />
  }

  if (!defaultBudget) {
    return null
  }

  return (
    <CurrentBudgetBox>
      <Heading level="4">
        <span>{t`Default Budget`}</span>
      </Heading>
      <div>
        <Link href={`/budget/${defaultBudget.id}/summary`}>
          <Flex alignItems="center" gap="10px">
            <BudgetPiggyBankIcon />
            <Text size="large">{defaultBudget.name}</Text>
          </Flex>
        </Link>
      </div>
      <br />
      <Flex justifyContent="right">
        <Button variant="outlined" display="monochrome" type="button" onClick={openModal}>
          <span>{t`All Budgets`}</span>
        </Button>
      </Flex>
    </CurrentBudgetBox>
  )
}
const BudgetHome = props => {
  const { budgetTitle } = useContext(Globals)
  const { data, isFetching } = useQuery(['budget-listing'], () => {
    return api.fetch('/budget/list')
  })
  const navigator = useNavigator()
  const reduxDispatch = useDispatch()
  const queryClient = useQueryClient()
  const [showModal, onToggleModal] = useState(false)
  const [editingRecord, setEditingRecord] = useState(null)
  const { onNewBudget } = props
  const defaultBudget = useMemo(() => {
    if (data?.budgets?.length > 0) {
      return data.budgets.find(budget => budget.selected) || data.budgets[0]
    }
  }, [data])
  const { mutate: startNewBudget, isLoading: isStartingBudget } = useMutation(
    isReverse => {
      return api.fetch(`/budget/new`, {
        method: 'POST',
        body: JSON.stringify({ isReverse })
      })
    },
    {
      onSuccess: budget => {
        const url = budget.reverse
          ? `/budget/${budget.id}/reverse`
          : `/budget/${budget.id}/location`

        navigator(url)
      },
      onError: () => {
        reduxDispatch(showUserFeedback(t`Could not start budget`, 'error'))
      }
    }
  )
  const { mutate: deleteBudget, isLoading: isDeletingBudget } = useMutation(
    budgetId => {
      return api.fetch(`/budget/${budgetId}`, {
        method: 'DELETE'
      })
    },
    {
      onSuccess: budgetId => {
        queryClient.setQueryData(['budget-listing'], queryData => {
          return queryData.filter(rec => rec.id !== budgetId)
        })
      },
      onError: () => {
        reduxDispatch(showUserFeedback(t`Could not delete budget`, 'error'))
      }
    }
  )
  const columns = [
    {
      id: 'name',
      label: t`Name`,
      width: '50%',
      render({ name, id }) {
        return editingRecord === id ? (
          <RenameBudgetForm
            budgetId={id}
            budgetName={name}
            cancelEdit={() => setEditingRecord(null)}
          />
        ) : (
          <Link href={`/budget/${id}/summary`}>{name}</Link>
        )
      }
    },
    {
      id: 'reverse',
      label: t`Direction`,
      render({ reverse, occupation }) {
        const lnk = occupation ? (
          <Link href={occupation.url}>{occupation.title}</Link>
        ) : (
          t`Salary`
        )

        return reverse ? jt`From ${lnk}` : t`Lifestyle`
      }
    },
    {
      id: 'amount',
      label: t`Expenses`,
      render({ amount }) {
        return amount.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })
      }
    },
    {
      id: 'id',
      label: t`Actions`,
      render({ name, id }) {
        return (
          <>
            <Button
              onClick={() => setEditingRecord(id)}
              variant="link"
              icon={EditIcon}
              style={{
                visibility: editingRecord !== id ? 'visible' : 'hidden'
              }}>
              <span className="sr-only">e</span>
            </Button>

            <Button
              isLoading={isDeletingBudget}
              variant="link"
              onClick={() => {
                if (confirm(t`Are you sure you want to delete: ${name}`)) {
                  deleteBudget(id)
                }
              }}
              icon={TrashIcon}>
              <span className="sr-only">x</span>
            </Button>
          </>
        )
      }
    }
  ]

  return (
    <Page>
      <Page.Title>{budgetTitle}</Page.Title>
      <Page.Layout>
        <Page.Content>
          <Text size="large">
            {t`Forming a budget is one of the most important parts of financial success. As you first enter the workforce, or even if you switch jobs, knowing how much money you will have available to spend on different aspects of your life will help you avoid debt and possibly even save some money for a rainy day.`}
          </Text>
          <PreviousBudget
            isLoading={isFetching}
            defaultBudget={defaultBudget}
            openModal={() => onToggleModal(true)}
          />

          <Heading level="2" align="center" style={{ margin: '25px 0' }}>
            {t`To get started, select an option below`}
          </Heading>

          <Flex gap="15px">
            <div className="text">
              <ForwardButton variant="link" onClick={() => startNewBudget(false)}>
                <span>{t`Begin`}</span>
              </ForwardButton>
              <Heading level="3">{t`Choose Your Lifestyle`}</Heading>
              <Text>{t`When you select this option, you will be asked a series of questions to help you determine the lifestyle you want to lead and how much it will cost you. When you are finished you can look at occupations that might support that lifestyle.`}</Text>
              <div>
                <Link
                  variant="outlined-cta"
                  isFullWidth={true}
                  size="large"
                  align="center"
                  href="/budget/location"
                  isLoading={isStartingBudget}
                  id="fowardlink">
                  {t`Budget how much money you'll need`}
                </Link>
              </div>
            </div>
            <div className="text">
              <ReverseButton variant="link" onClick={() => startNewBudget(true)}>
                <span>{t`Begin`}</span>
              </ReverseButton>
              <Heading level="3">{t`Pick Your Salary`}</Heading>
              <Text>{t`If you think you know how much money you are going to make, you can work backwards to see if that salary will support the lifestyle you want to lead. If it does not, you can search for alternative occupations that might.`}</Text>
              <div>
                <Link
                  isFullWidth={true}
                  variant="outlined-cta"
                  size="large"
                  align="center"
                  href="/budget/reverse"
                  isLoading={isStartingBudget}
                  id="reverselink">
                  {t`Budget from a starting salary`}
                </Link>
              </div>
            </div>
          </Flex>

          <Modal
            isOpen={showModal}
            onClose={() => {
              setEditingRecord(null)
              onToggleModal(false)
            }}>
            <Modal.Header closeButton>
              <Heading level="2">{t`All Budgets`}</Heading>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflow: 'scroll' }}>
              <Grid
                data={data?.budgets}
                columns={columns}
                options={{ disableFilters: true, fullWidth: true }}
                rowIdKey="id"
                defaultPageSize={25}
                isPagerVisible={false}
                isRowSelectable={false}
                dateRangePicker={false}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button onClick={() => onNewBudget(false)}>{t`Create New Budget`}</Button>
            </Modal.Footer>
          </Modal>
        </Page.Content>
      </Page.Layout>
    </Page>
  )
}

export { BudgetHome }
